import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiCloseLine, RiSaveLine, RiCheckLine, RiArrowRightLine, RiSparklingLine } from 'react-icons/ri';
import { PRESET_TEMPLATES } from './constants';
import { localizeFieldSchema } from '../../utils/sanitizeSchema';

// ─── Preview Modal ─────────────────────────────────────────────────────────────

function renderPreviewField(field, t) {
  const isRequired = field.required;
  const label = (
    <label className="fm-preview-label">
      {field.label || t('fieldsManager.modals.untitledField')}
      {isRequired && <span className="fm-required-star">*</span>}
    </label>
  );

  let input;
  switch (field.type) {
    case 'number':
      input = <input type="number" className="fm-preview-form-input" placeholder={field.placeholder || '0'} disabled />;
      break;
    case 'select':
      input = (
        <select className="fm-preview-form-select" disabled>
          <option value="">{t('fieldsManager.modals.selectAnOption')}</option>
          {field.options.map((o) => <option key={o.id} value={o.label}>{o.label}</option>)}
        </select>
      );
      break;
    case 'date':
      input = <input type="date" className="fm-preview-form-input" disabled />;
      break;
    case 'textarea':
      input = <textarea className="fm-preview-form-textarea" placeholder={field.placeholder || t('fieldsManager.modals.enterText')} rows={3} disabled />;
      break;
    case 'checkbox':
      return (
        <div key={field.id} className="fm-preview-field fm-preview-field--checkbox">
          <input type="checkbox" id={`prev-${field.id}`} disabled />
          <label htmlFor={`prev-${field.id}`}>
            {field.label || t('fieldsManager.modals.untitledField')}
            {isRequired && <span className="fm-required-star">*</span>}
          </label>
        </div>
      );
    case 'email':
      input = <input type="email" className="fm-preview-form-input" placeholder={field.placeholder || 'email@example.com'} disabled />;
      break;
    case 'phone':
      input = <input type="tel" className="fm-preview-form-input" placeholder={field.placeholder || '+1 (555) 000-0000'} disabled />;
      break;
    case 'currency':
      input = (
        <div className="fm-preview-currency-wrap">
          <span className="fm-preview-currency-symbol">$</span>
          <input type="number" className="fm-preview-form-input fm-preview-form-input--currency" placeholder="0.00" step="0.01" disabled />
        </div>
      );
      break;
    case 'file':
      input = <input type="file" className="fm-preview-form-file" disabled />;
      break;
    default:
      input = <input type="text" className="fm-preview-form-input" placeholder={field.placeholder || t('fieldsManager.modals.enterText')} disabled />;
  }

  return (
    <div key={field.id} className="fm-preview-field">
      {label}
      {input}
    </div>
  );
}

export function PreviewModal({ schema: rawSchema, schemaName, onClose }) {
  const { t, i18n } = useTranslation();
  // Field labels/placeholders/options may be multilang maps; resolve the
  // whole schema to the active language before rendering the preview.
  const schema = localizeFieldSchema(rawSchema, i18n.language);
  const totalFields = schema.sections.reduce((acc, s) => {
    return acc + (s.type === 'fields' ? (s.fields?.length || 0) : (s.tableColumns?.length || 0));
  }, 0);

  return (
    <div className="fm-modal-backdrop" onClick={onClose}>
      <div className="fm-modal fm-modal--preview" onClick={(e) => e.stopPropagation()}>
        <div className="fm-modal__header">
          <div>
            <h2 className="fm-modal__title">{t('fieldsManager.modals.formPreview')}</h2>
            <p className="fm-modal__subtitle">
              {schemaName || t('fieldsManager.untitledSchema')} &nbsp;·&nbsp;
              {schema.sections.length} section{schema.sections.length !== 1 ? 's' : ''} &nbsp;·&nbsp;
              {totalFields} field{totalFields !== 1 ? 's' : ''}
            </p>
          </div>
          <button className="fm-icon-btn fm-icon-btn--ghost" onClick={onClose} title={t('fieldsManager.modals.closePreview')}>
            <RiCloseLine />
          </button>
        </div>

        <div className="fm-modal__body fm-preview-body">
          {schema.sections.length === 0 && (
            <div className="fm-preview-empty-state">
              <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
              <p style={{ fontWeight: 600, color: '#334155', marginBottom: 8 }}>{t('fieldsManager.modals.nothingToPreview')}</p>
              <p style={{ color: '#94a3b8', fontSize: 13 }}>{t('fieldsManager.modals.addSectionsToPreview')}</p>
            </div>
          )}
          {schema.sections.map((section) => {
            // Passive sections are skipped on the real form — mirror that here.
            if (section.isActive === false) return null;
            if (section.type === 'table') {
              const cols = section.tableColumns || [];
              return (
                <div key={section.id} className="fm-preview-section" style={{ '--section-color': section.color }}>
                  <h3 className="fm-preview-section__title">{section.title || t('fieldsManager.modals.untitledSection')}</h3>
                  <div className="fm-preview-table-wrap">
                    <table className="fm-preview-table">
                      <thead>
                        <tr>
                          {cols.map((col) => (
                            <th key={col.id} className={`fm-preview-th fm-preview-th--${col.width}`}>
                              {col.header || t('fieldsManager.modals.untitled')}
                              {col.required && <span className="fm-required-star">*</span>}
                            </th>
                          ))}
                          <th className="fm-preview-th" />
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {cols.map((col) => (
                            <td key={col.id} className="fm-preview-td">
                              {col.type === 'select' ? (
                                <select className="fm-preview-select" disabled>
                                  <option>{t('fieldsManager.modals.select')}</option>
                                  {col.options.map((o) => <option key={o.id}>{o.label}</option>)}
                                </select>
                              ) : col.type === 'date' ? (
                                <input type="date" className="fm-preview-input" disabled />
                              ) : col.type === 'checkbox' ? (
                                <input type="checkbox" disabled />
                              ) : col.type === 'number' || col.type === 'currency' ? (
                                <input type="number" className="fm-preview-input" placeholder="0" disabled />
                              ) : (
                                <input type="text" className="fm-preview-input" placeholder="..." disabled />
                              )}
                            </td>
                          ))}
                          <td className="fm-preview-td">
                            <button className="fm-icon-btn fm-icon-btn--danger fm-icon-btn--sm" disabled>
                              <RiCloseLine />
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            }

            return (
              <div key={section.id} className="fm-preview-section" style={{ '--section-color': section.color }}>
                <h3 className="fm-preview-section__title">{section.title || t('fieldsManager.modals.untitledSection')}</h3>
                <div className="fm-preview-fields">
                  {section.fields.filter((f) => f.isActive !== false).map((field) => renderPreviewField(field, t))}
                  {section.fields.length === 0 && (
                    <p className="fm-preview-empty">{t('fieldsManager.modals.noFieldsInSection')}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="fm-modal__footer">
          <button className="fm-btn fm-btn--ghost" onClick={onClose}>
            <RiCloseLine /> {t('fieldsManager.modals.closePreview')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Template Picker Modal ─────────────────────────────────────────────────────

export function TemplatePickerModal({ onSelect, onClose }) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(null);

  return (
    <div className="fm-modal-backdrop" onClick={onClose}>
      <div className="fm-modal fm-modal--templates" onClick={(e) => e.stopPropagation()}>
        <div className="fm-modal__header">
          <div>
            <h2 className="fm-modal__title">{t('fieldsManager.modals.chooseTemplate')}</h2>
            <p className="fm-modal__subtitle">
              {t('fieldsManager.modals.pickReadyMadeForm')}
            </p>
          </div>
          <button className="fm-icon-btn fm-icon-btn--ghost" onClick={onClose} title={t('common.close')}>
            <RiCloseLine />
          </button>
        </div>

        <div className="fm-template-grid">
          {PRESET_TEMPLATES.map((tpl) => {
            const sectionCount = tpl.schema.sections.length;
            const fieldCount = tpl.schema.sections.reduce(
              (acc, s) => acc + (s.fields?.length || s.tableColumns?.length || 0), 0
            );
            return (
              <button
                key={tpl.id}
                className="fm-template-card"
                onClick={() => onSelect(tpl)}
                onMouseEnter={() => setHovered(tpl.id)}
                onMouseLeave={() => setHovered(null)}
                style={{ '--tpl-color': tpl.color }}
              >
                <div className="fm-template-card__icon">{tpl.icon}</div>
                <div className="fm-template-card__content">
                  <h4 className="fm-template-card__name">{tpl.name}</h4>
                  <p className="fm-template-card__desc">{tpl.description}</p>
                  <div className="fm-template-card__meta">
                    <span>📂 {sectionCount} section{sectionCount !== 1 ? 's' : ''}</span>
                    <span>📝 {fieldCount} field{fieldCount !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                <div className="fm-template-card__select-hint">
                  {t('fieldsManager.modals.useThisTemplate')} <RiArrowRightLine />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Schema Name Modal ─────────────────────────────────────────────────────────

export function SchemaNameModal({ initialName, onConfirm, onClose }) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName || '');
  const [description, setDescription] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onConfirm({ name: name.trim(), description: description.trim() });
  };

  return (
    <div className="fm-modal-backdrop" onClick={onClose}>
      <div className="fm-modal fm-modal--sm" onClick={(e) => e.stopPropagation()}>
        <div className="fm-modal__header">
          <div>
            <h2 className="fm-modal__title">{t('fieldsManager.modals.nameYourSchema')}</h2>
            <p className="fm-modal__subtitle">{t('fieldsManager.modals.giveItClearName')}</p>
          </div>
          <button className="fm-icon-btn fm-icon-btn--ghost" onClick={onClose}>
            <RiCloseLine />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="fm-modal__body">
            <div className="fm-name-modal__field">
              <label className="fm-name-modal__label">{t('fieldsManager.modals.schemaName')} <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                className="fm-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., NDA Custom Fields, Vendor Agreement..."
                autoFocus
                required
              />
            </div>
            <div className="fm-name-modal__field">
              <label className="fm-name-modal__label">{t('fieldsManager.modals.description')} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{t('fieldsManager.modals.optional')}</span></label>
              <textarea
                className="fm-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('fieldsManager.modals.descriptionPlaceholder')}
                rows={3}
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>
          <div className="fm-modal__footer">
            <button type="button" className="fm-btn fm-btn--ghost" onClick={onClose}>
              {t('fieldsManager.modals.cancel')}
            </button>
            <button type="submit" className="fm-btn fm-btn--primary" disabled={!name.trim()}>
              <RiSaveLine /> {t('fieldsManager.modals.saveAndContinue')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
